// Centre d'aide. Articles rédigés à partir du comportement réel du produit.
export const CATEGORIES = [
  { key: "demarrer", icon: "Sparkles", label: "Premiers pas", desc: "Créer un compte et prendre en main l'espace." },
  { key: "compte", icon: "Users", label: "Compte et profil", desc: "Connexion, informations personnelles, suppression." },
  { key: "coaching", icon: "Target", label: "Coaching", desc: "Réserver une séance et s'y préparer." },
  { key: "securite", icon: "ShieldCheck", label: "Sécurité et données", desc: "Protection du compte, exercice de vos droits." },
  { key: "technique", icon: "Code2", label: "Problèmes techniques", desc: "Quand quelque chose ne fonctionne pas." },
];

export const ARTICLES = [
  {
    slug: "creer-un-compte", category: "demarrer", updated: "16 septembre 2026",
    title: "Créer un compte sur Perform",
    short: "Il n'y a pas de formulaire d'inscription : votre compte se crée à la première connexion.",
    sections: [
      { heading: "Comment ça marche", blocks: [
        { t: "p", v: "Perform n'utilise pas de mot de passe. Vous entrez votre adresse e-mail, nous vous envoyons un lien, et votre compte est créé à la première ouverture de ce lien." },
        { t: "ol", v: [
          "Rendez-vous sur la page de connexion.",
          "Saisissez votre adresse e-mail et validez.",
          "Ouvrez le message reçu et cliquez sur « Ouvrir mon espace ».",
          "Complétez les trois étapes d'onboarding.",
        ]},
      ]},
      { heading: "Le lien expire", blocks: [
        { t: "p", v: "Un lien est valable **15 minutes** et ne fonctionne **qu'une seule fois**. Passé ce délai, redemandez-en un : c'est gratuit et illimité, dans la limite de cinq demandes par minute." },
      ]},
    ],
    related: ["lien-non-recu", "supprimer-mon-compte"],
  },
  {
    slug: "lien-non-recu", category: "compte", updated: "16 septembre 2026",
    title: "Je n'ai pas reçu mon lien de connexion",
    short: "Les causes les plus fréquentes et la marche à suivre.",
    sections: [
      { heading: "À vérifier dans l'ordre", blocks: [
        { t: "ol", v: [
          "**Le dossier indésirables.** C'est la cause la plus fréquente pour un premier message.",
          "**L'orthographe de l'adresse.** Une faute de frappe envoie le lien à quelqu'un d'autre, sans erreur visible de votre côté.",
          "**Le délai.** L'acheminement prend rarement plus d'une minute. Au-delà de cinq, quelque chose ne va pas.",
          "**Un filtre d'entreprise.** Certains serveurs bloquent les liens de connexion. Essayez une adresse personnelle.",
        ]},
      ]},
      { heading: "Toujours rien", blocks: [
        { t: "p", v: "Consultez la [page d'état](/statut) : si l'envoi d'e-mails est perturbé, l'incident y sera signalé. Sinon, écrivez à support@performcustom.com en précisant l'adresse utilisée et l'heure de votre demande." },
      ]},
    ],
    related: ["creer-un-compte", "proteger-mon-compte"],
  },
  {
    slug: "proteger-mon-compte", category: "securite", updated: "16 septembre 2026",
    title: "Protéger mon compte",
    short: "Sans mot de passe, la sécurité de votre compte est celle de votre boîte mail.",
    sections: [
      { heading: "Le point essentiel", blocks: [
        { t: "p", v: "Perform ne stocke aucun mot de passe : il n'y a donc rien à vous voler de ce côté. En contrepartie, **quiconque accède à votre boîte mail peut demander un lien de connexion.** Protéger votre messagerie, c'est protéger votre compte Perform." },
        { t: "ul", v: [
          "Activez la double authentification sur votre messagerie.",
          "Ne transférez jamais un lien de connexion : il ouvre votre session, pas la sienne.",
          "Déconnectez-vous sur un ordinateur partagé — cela supprime la session côté serveur, pas seulement le cookie.",
        ]},
      ]},
      { heading: "En cas de doute", blocks: [
        { t: "p", v: "Si vous pensez qu'une session vous a échappé, déconnectez-vous puis écrivez à securite@performcustom.com." },
      ]},
    ],
    related: ["lien-non-recu", "mes-donnees"],
  },
  {
    slug: "mes-donnees", category: "securite", updated: "16 septembre 2026",
    title: "Accéder à mes données ou les supprimer",
    short: "Vos droits RGPD et la manière de les exercer.",
    sections: [
      { heading: "Ce que nous détenons", blocks: [
        { t: "p", v: "Votre adresse e-mail, votre pseudo, vos prénom et nom si vous les avez renseignés, votre photo, votre jeu, votre palier et votre objectif. Rien d'autre. Le détail figure dans la [politique de confidentialité](/legal/confidentialite)." },
      ]},
      { heading: "Exercer vos droits", blocks: [
        { t: "p", v: "Écrivez à confidentialite@performcustom.com depuis l'adresse de votre compte, en précisant votre demande : accès, rectification, effacement, limitation, opposition ou portabilité. Nous répondons sous un mois." },
        { t: "p", v: "Vous pouvez également saisir la CNIL si notre réponse ne vous satisfait pas." },
      ]},
    ],
    related: ["supprimer-mon-compte", "proteger-mon-compte"],
  },
  {
    slug: "supprimer-mon-compte", category: "compte", updated: "16 septembre 2026",
    title: "Supprimer mon compte",
    short: "Définitif, et sans condition.",
    sections: [
      { heading: "Comment", blocks: [
        { t: "p", v: "Écrivez à support@performcustom.com depuis l'adresse de votre compte. La suppression est traitée sous 30 jours et entraîne l'effacement de votre profil, de vos sessions et de votre photo." },
        { t: "note", v: "La suppression est irréversible. Les séances déjà réalisées peuvent être conservées sous forme anonymisée pour nos obligations comptables, lorsque les paiements seront activés." },
      ]},
    ],
    related: ["mes-donnees"],
  },
  {
    slug: "preparer-sa-seance", category: "coaching", updated: "16 septembre 2026",
    title: "Bien préparer sa séance",
    short: "Ce qu'il faut apporter pour que l'heure serve vraiment.",
    sections: [
      { heading: "Choisir le format", blocks: [
        { t: "p", v: "Durée, module standard ou personnalisé, préparation à la compétition : chaque choix a un effet sur le tarif, détaillé sur la [page Tarifs](/tarifs)." },
      ]},
      { heading: "Ce qu'il faut apporter", blocks: [
        { t: "ul", v: [
          "Un objectif précis plutôt qu'un « je veux progresser ».",
          "Préparez une ou deux parties récentes qui illustrent votre difficulté.",
          "Prévoyez de quoi prendre des notes : une séance passe vite.",
        ]},
      ]},
    ],
    related: ["creer-un-compte"],
  },
  {
    slug: "probleme-technique", category: "technique", updated: "16 septembre 2026",
    title: "Quelque chose ne fonctionne pas",
    short: "Les réflexes utiles avant de nous écrire.",
    sections: [
      { heading: "Dans l'ordre", blocks: [
        { t: "ol", v: [
          "Consultez la [page d'état](/statut) : si le problème vient de nous, il y est signalé.",
          "Rechargez en vidant le cache (Cmd+Maj+R ou Ctrl+F5).",
          "Essayez une fenêtre de navigation privée : cela écarte une extension fautive.",
          "Vérifiez que votre navigateur est à jour.",
        ]},
      ]},
      { heading: "Nous écrire utilement", blocks: [
        { t: "p", v: "À support@performcustom.com, précisez : ce que vous faisiez, ce que vous attendiez, ce qui s'est produit, votre navigateur et l'heure approximative. Une capture aide beaucoup." },
      ]},
    ],
    related: ["lien-non-recu"],
  },
];

export const findArticle = slug => ARTICLES.find(a => a.slug === slug);
